import { useEffect, useState } from "react";
import { AlertCircle, Calculator, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi, messageFrom } from "@/lib/api";
import { money } from "@/lib/format";

const amountText = (amount, suffix) => {
  if (amount === null || amount === undefined) return "\u2014";
  return `${money(amount)}${suffix || ""}`;
};

const StaffOnlyNote = () => (
  <p className="flex items-start gap-2 rounded-lg border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-3 py-2 text-xs leading-relaxed text-[color:var(--tt-teal-deep)]">
    <Calculator className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    <span>
      Staff view only. The customer sees a single figure — these line items and derivations never
      appear on their quote, PDF or email.
    </span>
  </p>
);

/** Quotes issued before we stored derivations can only show the amounts. */
const LegacyNote = () => (
  <p
    data-testid="quote-workings-legacy-note"
    className="rounded-lg border border-dashed border-border px-3 py-2 text-[11.5px] leading-relaxed text-muted-foreground"
  >
    This quote was generated before we started storing the derivation for every line, so only the
    line items and amounts are available.
  </p>
);

/** The working behind one line, or the customer-facing note if that's all there is. */
const LineDetail = ({ line }) => {
  if (line.internal_detail) {
    return (
      <p
        data-testid="quote-workings-derivation"
        className="mt-1 border-l-2 border-[color:var(--tt-teal-line)] pl-2 text-[11.5px] leading-relaxed text-muted-foreground"
      >
        <span className="font-semibold text-[color:var(--tt-teal-deep)]">How we got there: </span>
        {line.internal_detail}
      </p>
    );
  }
  if (line.detail) {
    return (
      <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">{line.detail}</p>
    );
  }
  return null;
};

/** One priced line: what it is, what it came to, and how. */
const WorkingsLine = ({ line }) => (
  <div data-testid="quote-workings-line" className="px-3 py-2.5">
    <div className="flex items-start justify-between gap-3">
      <p className="text-[13px] font-semibold leading-snug text-foreground">{line.label}</p>
      <p
        data-testid="quote-workings-line-amount"
        className="shrink-0 whitespace-nowrap font-amount text-[13px] font-bold text-foreground"
      >
        {amountText(line.amount, line.suffix)}
      </p>
    </div>
    <LineDetail line={line} />
  </div>
);

/** A block of lines with its own subtotal. */
const WorkingsSection = ({ section }) => (
  <section
    data-testid="quote-workings-section"
    className="overflow-hidden rounded-lg border border-border"
  >
    <header className="border-b border-border bg-[color:var(--tt-surface-2)] px-3 py-2">
      <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-primary">
        {section.title}
      </p>
      {section.subtitle ? (
        <p className="mt-0.5 text-[11px] text-muted-foreground">{section.subtitle}</p>
      ) : null}
    </header>

    <div className="divide-y divide-border">
      {(section.lines || []).map((line, lineIndex) => (
        <WorkingsLine key={`${line.label}-${lineIndex}`} line={line} />
      ))}
    </div>

    <div className="flex items-center justify-between gap-3 border-t border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-3 py-2">
      <p className="text-[12px] font-bold text-[color:var(--tt-teal-deep)]">
        {section.subtotal_label || "Subtotal"}
      </p>
      <p
        data-testid="quote-workings-subtotal"
        className="whitespace-nowrap font-amount text-[12px] font-bold text-[color:var(--tt-teal-deep)]"
      >
        {amountText(section.subtotal, section.subtotal_suffix)}
      </p>
    </div>
  </section>
);

/** The figures the sections add up to, with the headline one emphasised. */
const WorkingsTotals = ({ totals }) => (
  <div className="overflow-hidden rounded-lg border border-border">
    {totals.map((total, index) => (
      <div
        key={`${total.label}-${index}`}
        data-testid="quote-workings-total"
        className={`flex items-center justify-between gap-3 px-3 py-2 ${
          total.emphasis
            ? "bg-primary text-white"
            : "border-t border-border bg-white text-foreground"
        }`}
      >
        <p
          className={`text-[12px] font-bold ${
            total.emphasis ? "uppercase tracking-[0.1em] text-white/80" : ""
          }`}
        >
          {total.label}
        </p>
        <p className="whitespace-nowrap font-amount text-[13px] font-bold">
          {amountText(total.amount, total.suffix)}
        </p>
      </div>
    ))}
  </div>
);

/**
 * TEAM ONLY — the full itemised derivation behind a quote.
 *
 * The customer only ever sees one figure (see pricing_engine.strip_internal),
 * so this is the single place the team can check every line item and exactly
 * how each figure was arrived at.
 */
export const QuoteWorkings = ({ quote, loading = false, error = null }) => {
  if (loading) {
    return (
      <div data-testid="quote-workings-loading" className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <p
        data-testid="quote-workings-error"
        className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] font-medium text-destructive"
      >
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {error}
      </p>
    );
  }

  const sections = quote?.sections || [];
  const totals = quote?.totals || [];

  if (!sections.length) {
    return (
      <p data-testid="quote-workings-empty" className="text-sm text-muted-foreground">
        No stored workings for this quote.
      </p>
    );
  }

  const hasDerivations = sections.some((section) =>
    (section.lines || []).some((line) => line.internal_detail),
  );

  return (
    <div data-testid="quote-workings" className="space-y-3">
      <StaffOnlyNote />
      {!hasDerivations ? <LegacyNote /> : null}

      {sections.map((section, sectionIndex) => (
        <WorkingsSection key={`${section.title}-${sectionIndex}`} section={section} />
      ))}

      {totals.length ? <WorkingsTotals totals={totals} /> : null}
    </div>
  );
};

/** Fetches the stored quote for a reference, then renders its workings. */
export const QuoteWorkingsByRef = ({ quoteRef }) => {
  const [state, setState] = useState({ loading: false, quote: null, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!quoteRef) {
      setState({ loading: false, quote: null, error: null });
      return undefined;
    }
    setState({ loading: true, quote: null, error: null });
    adminApi
      .quoteDetail(quoteRef)
      .then((data) => {
        if (cancelled) return;
        setState({ loading: false, quote: data?.quote || null, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          quote: null,
          error: messageFrom(err, "We couldn't load the workings for this quote."),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [quoteRef]);

  if (!quoteRef) return null;

  return <QuoteWorkings quote={state.quote} loading={state.loading} error={state.error} />;
};

export const QuoteWorkingsSpinner = () => (
  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
);
